import React from 'react';
import { useThemeContext } from '../context/ThemeProvider';
import { useLanguage } from '../context/LanguageProvider';

interface SeedStatusIndicatorProps {
  selectedPattern?: string;
  isGridModified?: boolean;
  hasGridContent?: boolean;
  forceRandom?: boolean;
}

interface SeedStatus {
  type: 'random' | 'pattern' | 'grid';
  icon: string;
  text: string;
  tooltip: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

/**
 * Seed狀態指示器
 * 顯示當前DrumRNN使用的seed類型
 */
const SeedStatusIndicator: React.FC<SeedStatusIndicatorProps> = ({ 
  selectedPattern, 
  isGridModified, 
  hasGridContent,
  forceRandom = false
}) => {
  const { getClass } = useThemeContext();
  const { t } = useLanguage();

  // 決定seed類型和顯示內容
  const getSeedStatus = (): SeedStatus => {
    // 強制隨機模式優先
    if (forceRandom) {
      return {
        type: 'random',
        icon: '🎲',
        text: t('drumRNN.forceRandom') || '強制隨機',
        tooltip: t('drumRNN.forceRandomTooltip') || '強制隨機生成，忽略所有 seed',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-300'
      };
    }
    
    if (selectedPattern && !isGridModified) {
      return {
        type: 'pattern',
        icon: '🌱',
        text: t(`drumPatterns.${selectedPattern}`) || selectedPattern,
        tooltip: (t('drumRNN.usePatternSeed') || '使用 "{pattern}" pattern 作為 seed').replace('{pattern}', selectedPattern),
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-300'
      };
    } else if (hasGridContent) {
      return {
        type: 'grid',
        icon: '🔄',
        text: isGridModified ? (t('drumRNN.customGrid') || '自定義網格') : (t('drumRNN.currentGrid') || '當前網格'),
        tooltip: t('drumRNN.useGridSeed') || '使用當前網格內容作為 seed',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-300'
      };
    } else {
      return {
        type: 'random',
        icon: '🎲',
        text: t('drumRNN.randomGeneration') || '隨機生成',
        tooltip: t('drumRNN.randomGenerationTooltip') || '純隨機生成，不使用 seed',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        borderColor: 'border-gray-300'
      };
    }
  };

  const status = getSeedStatus();

  return (
    <div 
      className={`
        relative group inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
        ${status.bgColor} ${status.textColor} ${status.borderColor} border
        transition-all duration-200 cursor-help
      `}
      title={status.tooltip}
    >
      <span className="text-sm">{status.icon}</span>
      <span className="hidden sm:inline">{status.text}</span>
      <span className="sm:hidden">Seed</span>
      {/* 桌面版tooltip */}
      <div className="absolute invisible group-hover:visible bg-black text-white text-xs rounded px-2 py-1 bottom-full mb-1 whitespace-nowrap z-10 left-1/2 transform -translate-x-1/2">
        {status.tooltip}
      </div>
    </div>
  );
};

export default SeedStatusIndicator; 